// src/Sidebar.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { FILING_STATUS } from '../hooks/usePlanTracker';
import './TaxApp.css';

// ── Status tag config ─────────────────────────────────────────────────────────
// FAXED     = faxId received, delivery in progress
// DELIVERED = polling confirmed delivery
// NOT_FAXED = generated but not sent
// DRAFT     = in progress
const STATUS_TAG = {
  [FILING_STATUS.DRAFT]: {
    label: 'Draft',
    className: 'status-tag status-tag--draft',
  },
  [FILING_STATUS.NOT_FAXED]: {
    label: 'Not faxed',
    className: 'status-tag status-tag--not-faxed',
  },
  [FILING_STATUS.FAXED]: {
    label: 'Faxed',
    className: 'status-tag status-tag--faxed',
  },
  [FILING_STATUS.DELIVERED]: {
    label: 'Delivered',
    className: 'status-tag status-tag--delivered',
  },
  [FILING_STATUS.FILED]: {
    label: 'Filed',
    className: 'status-tag status-tag--filed',
  },
};

function StatusTag({ status }) {
  const cfg = STATUS_TAG[status];
  if (!cfg) return null;
  return <span className={cfg.className}>{cfg.label}</span>;
}

// Truncate faxId for display: "01KPFAX8JK...80QKJG"
function shortFaxId(faxId) {
  if (!faxId) return null;
  if (faxId.length <= 16) return faxId;
  return `${faxId.slice(0, 10)}…${faxId.slice(-6)}`;
}

function Sidebar({
  user,
  chatHistory,
  isHistoryLoading,
  currentSessionId,
  usageStats,
  planTracker,
  onNewChat,
  onLoadSession,
  onDeleteSession,
  onLogout,
  getRelativeTime,
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [expanded,     setExpanded    ] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  if (!user) return null;

  const loading     = planTracker?.loading ?? true;
  const plan        = planTracker?.currentPlan || null;
  const hasPlan     = plan === 'PAID' || plan === 'LIFETIME';
  const isUnlimited = plan === 'LIFETIME';
  const filingsUsed = usageStats?.filingsUsed ?? 0;
  const planLabel   = plan === 'LIFETIME' ? 'Lifetime' : plan === 'PAID' ? 'Single Filing' : null;

  const usageBarPercent = () => {
    if (!hasPlan || isUnlimited) return 100;
    return Math.min(filingsUsed * 100, 100);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowDropdown(false);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={`sidebar ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-content">

        {/* TOP */}
        <div className="sidebar-top">
          <div className="sidebar-header">
            {expanded && (
              <div className="sidebar-logo">
                <img src="/logo192.png" alt="Logo" className="logo-image-sidebar" />
                <h1 className="logo-text-sidebar">Taxsuites</h1>
              </div>
            )}
            <button
              className={`sidebar-toggle ${!expanded ? 'sidebar-toggle--collapsed' : ''}`}
              onClick={() => setExpanded(!expanded)}
              title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
              </svg>
            </button>
          </div>

          {expanded && (
            <>
              <button className="new-chat-btn" onClick={onNewChat}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>New Filing</span>
              </button>

              <div className="chat-history">
                <div className="chat-history-header">Recent Filings</div>

                {isHistoryLoading ? (
                  <div className="history-loading">
                    <div className="spinner-small" />
                    <span>Loading...</span>
                  </div>
                ) : chatHistory?.length > 0 ? (
                  chatHistory.map(chat => (
                    <div
                      key={chat.id}
                      className={`chat-item ${currentSessionId === chat.id ? 'active' : ''}`}
                      onClick={() => onLoadSession(chat)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>

                      <div className="chat-item-content">
                        <div className="chat-item-title">{chat.title}</div>
                        <div className="chat-item-meta">
                          {/* Status: Draft / Not faxed / Faxed / Delivered */}
                          <StatusTag status={chat.status} />

                          {/* Show faxId as soon as it's available, even before delivery */}
                          {chat.faxId && (
                            <span
                              className="chat-item-faxid"
                              title={chat.faxId}
                            >
                              #{shortFaxId(chat.faxId)}
                            </span>
                          )}

                          <span className="chat-item-time">
                            {getRelativeTime(
                              chat.updatedAt instanceof Date ? chat.updatedAt :
                              chat.timestamp instanceof Date ? chat.timestamp :
                              new Date(chat.timestamp)
                            )}
                          </span>
                        </div>
                      </div>

                      <button
                        className="delete-session-btn"
                        onClick={(e) => { e.stopPropagation(); onDeleteSession(chat.id, e); }}
                        title="Delete filing"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-history">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>No filings yet</p>
                    <span>Your filings will appear here</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* BOTTOM */}
        <div className="sidebar-bottom">
          {expanded ? (
            <>
              {!loading && hasPlan && (
                <div className="sidebar-usage-card">
                  <div className="sidebar-usage-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{planLabel}</span>
                  </div>
                  <div className="sidebar-usage-stats">
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">Filed</span>
                      <span className="sidebar-usage-value">{filingsUsed}</span>
                    </div>
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">Remaining</span>
                      <span className="sidebar-usage-value">
                        {isUnlimited ? 'Unlimited' : Math.max(0, 1 - filingsUsed)}
                      </span>
                    </div>
                  </div>
                  {!isUnlimited && (
                    <div className="sidebar-usage-progress">
                      <div
                        className="sidebar-usage-progress-bar"
                        style={{ width: `${usageBarPercent()}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {!loading && !hasPlan && (
                <div className="sidebar-usage-card">
                  <div className="sidebar-usage-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="plan-pill plan-pill--free">Free plan</span>
                  </div>
                  <button className="sidebar-upgrade-btn" onClick={() => navigate('/pricing-plans')}>
                    Get started — $50
                  </button>
                </div>
              )}

              <div className="profile-dropdown-container">
                <button
                  className="sidebar-menu-item user-profile"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <div className="user-avatar">
                    <span className="user-initials">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="user-info">
                    <div className="user-name">{user?.displayName || user?.email?.split('@')[0]}</div>
                    <div className="user-email">{user?.email}</div>
                  </div>
                  <svg className="user-dropdown" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                {showDropdown && (
                  <div className="profile-dropdown-menu">
                    <button className="profile-dropdown-item logout" onClick={handleLogout}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button className="sidebar-icon-btn" onClick={() => setExpanded(true)}>
              <span className="user-initials-small">{user.email.charAt(0).toUpperCase()}</span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

export default Sidebar;